import {z} from "zod";

import {VEHICLE_SERVICE_REQUEST_IDS} from "../types";

const nextCalendarYear = new Date().getFullYear() + 1;

export const companyVehicleStatusSchema = z.enum([
  "active",
  "inactive",
  "maintenance",
  "retired",
]);

export const vehicleFuelLevelSchema = z.enum([
  "empty",
  "quarter",
  "half",
  "three_quarters",
  "full",
]);
export type VehicleFuelLevel = z.infer<typeof vehicleFuelLevelSchema>;

/**
 * Empty string = no selection yet. Required so Base UI `Select` stays controlled from the first
 * render (`value` must not start as `undefined` then become a string).
 */
export const vehicleConditionCheckFuelLevelFormSchema = z
  .union([z.literal(""), vehicleFuelLevelSchema])
  .refine((v) => v !== "", {message: "Select a fuel level"});

export const companyVehicleCreateFormSchema = z
  .object({
    property: z.any().nullish(),
    label: z.string().optional(),
    unit_number: z.string().optional(),
    plate: z.string().max(30).optional(),
    vin: z.string().max(50).optional(),
    make: z.string(),
    model: z.string(),
    vehicle_make_id: z.number().nullish(),
    vehicle_model_id: z.number().nullish(),
    year: z.preprocess(
      (v) => {
        if (v === "" || v === null || v === undefined) return undefined;
        const n = Number(v);
        return Number.isNaN(n) ? undefined : n;
      },
      z.number().int().min(1900).max(nextCalendarYear).optional(),
    ),
    current_mileage: z.preprocess(
      (v) => (v === "" || v === null || v === undefined ? undefined : Number(v)),
      z.number().nonnegative().optional(),
    ),
  })
  .superRefine((data, ctx) => {
    const makeId = data.vehicle_make_id;
    const modelId = data.vehicle_model_id;
    const hasMake = makeId != null && Number.isFinite(makeId);
    const hasModel = modelId != null && Number.isFinite(modelId);
    if (hasModel && !hasMake) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Select a catalog make before choosing a catalog model.",
        path: ["model"],
      });
    }
    const u = (data.unit_number ?? "").trim();
    const l = (data.label ?? "").trim();
    const p = (data.plate ?? "").trim();
    if (!u && !l && !p) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Enter at least a unit number, label, or license plate.",
        path: ["plate"],
      });
    }
  });

export type CompanyVehicleCreateFormValues = z.infer<typeof companyVehicleCreateFormSchema>;

const vehicleServiceRequestIdSchema = z.enum(
  VEHICLE_SERVICE_REQUEST_IDS as unknown as [string, ...string[]],
);

const vehicleConditionFileOrUploadedUrl = z.union([
  z.string().min(1),
  z.custom<File>((v) => v instanceof File),
]);

export const vehicleConditionCheckFormSchema = z.object({
  company_vehicle_id: z.coerce
    .number()
    .int()
    .positive("Select a company vehicle"),
  odometer_miles: z.preprocess(
    (v) => (v === "" || v === null || v === undefined ? undefined : Number(v)),
    z.number().nonnegative().optional(),
  ),
  fuel_level: vehicleConditionCheckFuelLevelFormSchema,
  service_requests: z
    .array(vehicleServiceRequestIdSchema)
    .max(5)
    .default([]),
  notes: z.string().optional(),
  files: z.array(vehicleConditionFileOrUploadedUrl).default([]),
});

/** Use `z.input` so `fuel_level` includes `""` (controlled Select empty state) before validation. */
export type VehicleConditionCheckFormValues = z.input<typeof vehicleConditionCheckFormSchema>;
